import { LOG_LEVELS, logMessage } from '../logging.js';
import { produce } from 'immer';

class StateManager {
  constructor() {
    if (!StateManager.instance) {
      this.state = {
        isMobileDevice: false,
        frontImageSelected: false,
        reinitialize: false,
        currentCardFrontCount: null,
        dropzoneFiles: 0,
        stopProcessing: false,
        newCardArrived: false,
        firstCard: true,
        frontStreamDone: false,
        currentCardFront: null,
        clickActionTaken: false,
        alertOnMismatch: false,
        popupOnDisplay: false,
        newUser: true,
        currentFrontRequestId: 0,
        currentFrontImage: null,
        currentFrontCoords: null,
        frontRotation: 0,
        userModified: false,
        inRescan: false,
        frontInErrorState: false,
        subscription_level: null,
        phaseOneData: null,
        inPhase2: false,
        defaultCollection: null,
        cardState: {
          cardFronts: {},
        },
        userObj: null,
      };
      this.subscribers = [];
      StateManager.instance = this;
    }
    return StateManager.instance;
  }

  updateState(newState) {
    this.updateStateWithoutNotify(newState);
    this.notifySubscribers();
  }

  updateStateWithoutNotify(newState) {
    this.oldState = { ...this.state }; // Store the old state

    const nextState = produce(this.state, (draftState) => {
      Object.assign(draftState, newState);
    });
    this.state = nextState;
  }

  notifySubscribers() {
    this.subscribers.forEach((callback) => {
      if (typeof callback === 'function') {
        callback(this.state, this.oldState);
      } else {
        console.error('Invalid subscriber:', callback);
      }
    });
  }

  getState() {
    return this.state;
  }

  addCardFronts(requestId, frontImage) {
    logMessage(LOG_LEVELS.DEBUG, 'Adding card front: ', {
      requestId,
      frontImage,
    });

    this.state = produce(this.state, (draftState) => {
      if (!draftState.cardState.cardFronts[requestId]) {
        draftState.cardState.cardFronts[requestId] = [];
      }
      draftState.cardState.cardFronts[requestId].push(frontImage);
    });
  }

  getNextCard(frontRequestId) {
    logMessage(LOG_LEVELS.DEBUG, 'Dequeueing card.', {
      frontRequestId,
    });

    if (!this.hasCard(frontRequestId)) {
      logMessage(LOG_LEVELS.ERROR, 'No cards for the provided request IDs!');
      return { currentCardFront: null };
    }

    const { cardFronts } = this.state.cardState;
    const currentCardFront = cardFronts[frontRequestId][0];

    this.setCurrentCards(currentCardFront);
    this.removeFirstCardData(frontRequestId);

    return { currentCardFront };
  }

  hasCard(frontRequestId) {
    const frontQueueLength = this.queueLength(frontRequestId);

    logMessage(LOG_LEVELS.DEBUG, 'Checking card existence', {
      frontRequestId,
      frontQueueLength,
    });

    return frontQueueLength > 0;
  }

  queueLength(frontRequestId) {
    const { cardFronts } = this.state.cardState;

    const frontQueue = cardFronts[frontRequestId] || [];

    const frontQueueLength = frontQueue.length;

    logMessage(LOG_LEVELS.DEBUG, 'Queue lengths for request IDs', {
      frontRequestId,
      frontQueueLength,
    });

    return frontQueueLength;
  }

  // Helper method to update the current cards without notifying subscribers
  setCurrentCards(frontCard) {
    this.updateStateWithoutNotify({
      currentCardFront: frontCard,
    });
  }

  // Helper method to remove the first element from the lists associated with the request IDs
  removeFirstCardData(frontRequestId) {
    this.state = produce(this.state, (draftState) => {
      draftState.cardState.cardFronts[frontRequestId] =
        draftState.cardState.cardFronts[frontRequestId].slice(1);
    });
  }

  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  // Implement other methods here, based on your existing functions
}

const instance = new StateManager();

export default instance;
